import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Grid,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Chip,
    IconButton,
    TextField,
    Button,
    Collapse,
    Stack,
    MenuItem,
    FormControlLabel,
    Switch,
    Avatar,
    Fade,
    useTheme,
    Autocomplete,
    Divider
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    KeyboardArrowDown as ArrowDownIcon,
    KeyboardArrowUp as ArrowUpIcon,
    BugReport as BugIcon,
    Warning as WarningIcon,
    Info as InfoIcon,
    Route as RouteIcon,
    ErrorOutline as ErrorIcon,
    History as HistoryIcon,
    Group as GroupIcon,
    CalendarMonth as CalendarIcon,
    Search as SearchIcon,
    Build as BuildIcon
} from '@mui/icons-material';
import PatchInfoModal, { ErrorLog } from './PatchInfoModal';
import ReactApexChart from 'react-apexcharts';
import { format, subDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import api from '../../../services/api';
import LottieLoading from '../../../components/common/LottieLoading';
import { User } from '../../../types/auth';

interface StatsData {
    daily: {
        _id: string;
        count: number;
        errorCount: number;
        warningCount: number;
        apiTraceCount: number;
    }[];
    byLevel: {
        _id: string;
        count: number;
    }[];
}

const ErrorReporterPage = () => {
    const theme = useTheme();
    const [stats, setStats] = useState<StatsData | null>(null);
    const [logs, setLogs] = useState<ErrorLog[]>([]);
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [total, setTotal] = useState(0);
    const [expandedRow, setExpandedRow] = useState<string | null>(null);

    // Filters
    const [levelFilter, setLevelFilter] = useState('all');
    const [daysFilter, setDaysFilter] = useState(7);
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const [searchQuery, setSearchQuery] = useState('');
    const [showApiTraces, setShowApiTraces] = useState(false);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);

    // Patch Modal State
    const [patchModalOpen, setPatchModalOpen] = useState(false);
    const [selectedErrorForPatch, setSelectedErrorForPatch] = useState<ErrorLog | null>(null);

    const fetchData = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams({
                page: (page + 1).toString(),
                limit: rowsPerPage.toString(),
                level: levelFilter,
                search: searchQuery,
                includeApiTraces: showApiTraces.toString(),
                userId: selectedUser?._id || 'all'
            });

            if (startDate) params.append('startDate', startDate);
            if (endDate) params.append('endDate', endDate);
            if (!startDate && !endDate) params.append('days', daysFilter.toString());

            const statsParams = new URLSearchParams({
                days: daysFilter.toString(),
                includeApiTraces: showApiTraces.toString(),
                userId: selectedUser?._id || 'all'
            });
            if (startDate) statsParams.append('startDate', startDate);
            if (endDate) statsParams.append('endDate', endDate);

            const [statsRes, logsRes] = await Promise.all([
                api.get(`/api/errors/stats?${statsParams.toString()}`),
                api.get(`/api/errors?${params.toString()}`)
            ]);
            setStats(statsRes.data);
            setLogs(logsRes.data.data);
            setTotal(logsRes.data.pagination.total);
        } catch (err) {
            console.error("Failed to fetch error data", err);
        } finally {
            setLoading(false);
        }
    };

    const fetchUsers = async () => {
        try {
            const res = await api.get('/api/users');
            setUsers(res.data);
        } catch (err) {
            console.error("Error fetching users", err);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    useEffect(() => {
        fetchData();
    }, [page, rowsPerPage, levelFilter, daysFilter, showApiTraces, selectedUser]);

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        setPage(0);
        fetchData();
    };

    const handleChangePage = (_event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleOpenPatchModal = (error: ErrorLog) => {
        setSelectedErrorForPatch(error);
        setPatchModalOpen(true);
    };

    const handleSavePatch = async (data: Partial<ErrorLog>) => {
        if (!selectedErrorForPatch) return;

        try {
            const res = await api.put(`/api/errors/${selectedErrorForPatch._id}`, data);

            // Update local state
            setLogs(prevLogs => prevLogs.map(log =>
                log._id === selectedErrorForPatch._id ? { ...log, ...res.data } : log
            ));

            setPatchModalOpen(false);
            setSelectedErrorForPatch(null);
        } catch (err) {
            console.error("Failed to update patch info", err);
            // Optionally add toast notification here
        }
    };

    const getLevelColor = (level: string) => {
        switch (level) {
            case 'error': return 'error';
            case 'warning': return 'warning';
            case 'info': return 'info';
            case 'apitrace': return 'success';
            default: return 'default';
        }
    };

    const getLevelIcon = (level: string) => {
        switch (level) {
            case 'error': return <BugIcon fontSize="small" />;
            case 'warning': return <WarningIcon fontSize="small" />;
            case 'info': return <InfoIcon fontSize="small" />;
            case 'apitrace': return <RouteIcon fontSize="small" />;
            default: return <InfoIcon fontSize="small" />;
        }
    };

    // Chart Configuration
    const lineChartOptions: ApexCharts.ApexOptions = {
        chart: { type: 'area', toolbar: { show: false }, zoom: { enabled: false } },
        stroke: { curve: 'smooth', width: 3 },
        xaxis: {
            categories: stats?.daily.map(d => format(new Date(d._id), 'dd/MM', { locale: fr })) || [],
            labels: { style: { colors: '#64748b' } }
        },
        yaxis: { labels: { style: { colors: '#64748b' } } },
        colors: ['#ef5350', '#ff9800'],
        fill: {
            type: 'gradient',
            gradient: {
                shadeIntensity: 1,
                opacityFrom: 0.45,
                opacityTo: 0.05,
                stops: [20, 100, 100, 100]
            }
        },
        dataLabels: { enabled: false },
        legend: { position: 'top', horizontalAlign: 'right' },
        grid: { borderColor: '#f1f5f9' }
    };

    const lineChartSeries = [
        { name: 'Erreurs', data: stats?.daily.map(d => d.errorCount) || [] },
        { name: 'Avertissements', data: stats?.daily.map(d => d.warningCount) || [] }
    ];

    const apiTraceChartOptions: ApexCharts.ApexOptions = {
        ...lineChartOptions,
        colors: ['#4caf50'],
        fill: {
            ...lineChartOptions.fill,
            gradient: {
                ...lineChartOptions.fill?.gradient as any,
                opacityFrom: 0.45,
                opacityTo: 0.05,
            }
        }
    };

    const apiTraceChartSeries = [
        { name: 'API Traces', data: stats?.daily.map(d => d.apiTraceCount) || [] }
    ];

    const filteredByLevel = stats?.byLevel.filter(d => d._id !== 'apitrace') || [];

    const donutChartOptions: ApexCharts.ApexOptions = {
        labels: filteredByLevel.map(d => d._id.toUpperCase()) || [],
        colors: filteredByLevel.map(d => {
            if (d._id === 'error') return '#ef5350';
            if (d._id === 'warning') return '#ff9800';
            if (d._id === 'info') return '#03a9f4';
            return '#94a3b8';
        }),
        legend: { position: 'bottom' },
        stroke: { width: 0 },
        plotOptions: {
            pie: {
                donut: {
                    size: '75%',
                    labels: {
                        show: true,
                        total: {
                            show: true,
                            label: 'Total',
                            formatter: (w) => w.globals.seriesTotals.reduce((a: number, b: number) => a + b, 0).toString()
                        }
                    }
                }
            }
        }
    };

    const donutChartSeries = filteredByLevel.map(d => d.count) || [];

    if (loading && !stats && logs.length === 0) {
        return <LottieLoading message="Chargement des rapports d'erreurs..." />;
    }

    const totalErrors = stats?.byLevel.find(s => s._id === 'error')?.count || 0;
    const totalWarnings = stats?.byLevel.find(s => s._id === 'warning')?.count || 0;
    const totalApiTraces = stats?.byLevel.find(s => s._id === 'apitrace')?.count || 0;

    return (
        <Box sx={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e9f2 100%)',
            p: { xs: 2, md: 4 }
        }}>
            {/* Hero Header */}
            <Paper
                elevation={0}
                sx={{
                    background: 'linear-gradient(135deg, #FF4B2B 0%, #FF416C 100%)',
                    borderRadius: 4,
                    p: { xs: 3, md: 5 },
                    mb: 4,
                    color: 'white',
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                {/* Decorative circles */}
                <Box sx={{
                    position: 'absolute',
                    top: -50,
                    right: -50,
                    width: 200,
                    height: 200,
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.1)',
                }} />

                <Grid container spacing={4} alignItems="center">
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Avatar sx={{
                                bgcolor: 'rgba(255,255,255,0.2)',
                                width: 56,
                                height: 56,
                                backdropFilter: 'blur(10px)'
                            }}>
                                <ErrorIcon sx={{ fontSize: 28 }} />
                            </Avatar>
                            <Box>
                                <Typography variant="h4" sx={{ fontWeight: 700, fontFamily: 'Quicksand, sans-serif' }}>
                                    Error Reporter
                                </Typography>
                                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                                    Surveillez et analysez les erreurs de l'application en temps réel
                                </Typography>
                            </Box>
                        </Box>
                        <Button
                            variant="contained"
                            size="large"
                            startIcon={<RefreshIcon />}
                            onClick={fetchData}
                            sx={{
                                mt: 2,
                                bgcolor: 'white',
                                color: '#FF416C',
                                fontWeight: 700,
                                px: 4,
                                py: 1.5,
                                borderRadius: 3,
                                textTransform: 'none',
                                fontSize: '1rem',
                                boxShadow: '0 4px 14px rgba(0,0,0,0.2)',
                                '&:hover': {
                                    bgcolor: 'rgba(255,255,255,0.95)',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
                                },
                                transition: 'all 0.2s ease'
                            }}
                        >
                            Actualiser
                        </Button>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 4 }}>
                                <Box sx={{
                                    bgcolor: 'rgba(255,255,255,0.15)',
                                    backdropFilter: 'blur(10px)',
                                    borderRadius: 3,
                                    p: 2,
                                    textAlign: 'center',
                                }}>
                                    <Typography variant="h3" sx={{ fontWeight: 700, fontSize: { xs: '1.5rem', md: '2.5rem' } }}>{total}</Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>Total Logs</Typography>
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 4 }}>
                                <Box sx={{
                                    bgcolor: 'rgba(255,255,255,0.15)',
                                    backdropFilter: 'blur(10px)',
                                    borderRadius: 3,
                                    p: 2,
                                    textAlign: 'center',
                                }}>
                                    <Typography variant="h3" sx={{ fontWeight: 700, fontSize: { xs: '1.5rem', md: '2.5rem' }, color: '#fff' }}>{totalErrors}</Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>Erreurs</Typography>
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 4 }}>
                                <Box sx={{
                                    bgcolor: 'rgba(255,255,255,0.15)',
                                    backdropFilter: 'blur(10px)',
                                    borderRadius: 3,
                                    p: 2,
                                    textAlign: 'center',
                                }}>
                                    <Typography variant="h3" sx={{ fontWeight: 700, fontSize: { xs: '1.5rem', md: '2.5rem' } }}>{totalWarnings}</Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>Alerte</Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Paper>

            {/* Charts Section */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid size={{ xs: 12, md: 8 }}>
                    <Card sx={{ borderRadius: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid rgba(255,255,255,0.3)' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <HistoryIcon color="primary" />
                                    <Typography variant="h6" fontWeight="700">Évolution temporelle</Typography>
                                </Box>
                                <TextField
                                    select
                                    size="small"
                                    value={daysFilter}
                                    onChange={(e) => setDaysFilter(Number(e.target.value))}
                                    sx={{ width: 180 }}
                                    disabled={!!startDate || !!endDate}
                                >
                                    <MenuItem value={7}>7 derniers jours</MenuItem>
                                    <MenuItem value={15}>15 derniers jours</MenuItem>
                                    <MenuItem value={30}>30 derniers jours</MenuItem>
                                    <MenuItem value={90}>3 derniers mois</MenuItem>
                                </TextField>
                            </Box>
                            <ReactApexChart options={lineChartOptions} series={lineChartSeries} type="area" height={300} />
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <Card sx={{ height: '100%', borderRadius: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid rgba(255,255,255,0.3)' }}>
                        <CardContent>
                            <Typography variant="h6" fontWeight="700" gutterBottom>Répartition par gravité</Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'center', height: 300, alignItems: 'center' }}>
                                <ReactApexChart options={donutChartOptions} series={donutChartSeries} type="donut" width="100%" />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* API Trace Chart (Conditional) */}
            {showApiTraces && (
                <Fade in timeout={500}>
                    <Card sx={{ mb: 4, borderRadius: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid rgba(255,255,255,0.3)' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                                <RouteIcon color="success" />
                                <Typography variant="h6" fontWeight="700">Évolution API Traces ({totalApiTraces})</Typography>
                            </Box>
                            <ReactApexChart options={apiTraceChartOptions} series={apiTraceChartSeries} type="area" height={250} />
                        </CardContent>
                    </Card>
                </Fade>
            )}

            {/* Filters Section */}
            <Paper sx={{ mb: 4, p: 3, borderRadius: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                <Grid container spacing={2} component="form" onSubmit={handleSearch} alignItems="flex-end">
                    <Grid size={{ xs: 12, md: 3 }}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Recherche"
                            placeholder="Message, Path, Method..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            slotProps={{
                                input: {
                                    startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 20 }} />
                                }
                            }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 2 }}>
                        <TextField
                            select
                            fullWidth
                            size="small"
                            label="Niveau"
                            value={levelFilter}
                            onChange={(e) => setLevelFilter(e.target.value)}
                        >
                            <MenuItem value="all">Tous les niveaux</MenuItem>
                            <MenuItem value="error">Erreur</MenuItem>
                            <MenuItem value="warning">Avertissement</MenuItem>
                            <MenuItem value="info">Information</MenuItem>
                            <MenuItem value="apitrace">API Trace</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid size={{ xs: 12, md: 3 }}>
                        <Autocomplete
                            size="small"
                            options={users}
                            getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.email})`}
                            value={selectedUser}
                            onChange={(_e, newValue) => setSelectedUser(newValue)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Filtrer par utilisateur"
                                    fullWidth
                                    slotProps={{
                                        input: {
                                            ...params.InputProps,
                                            startAdornment: (
                                                <>
                                                    <GroupIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 20 }} />
                                                    {params.InputProps.startAdornment}
                                                </>
                                            )
                                        }
                                    }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <TextField
                                type="date"
                                size="small"
                                label="Début"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                slotProps={{ inputLabel: { shrink: true } }}
                                fullWidth
                            />
                            <TextField
                                type="date"
                                size="small"
                                label="Fin"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                slotProps={{ inputLabel: { shrink: true } }}
                                fullWidth
                            />
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, md: 10 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={showApiTraces}
                                    onChange={(e) => setShowApiTraces(e.target.checked)}
                                    color="success"
                                />
                            }
                            label={<Typography variant="body2" fontWeight="500">Inclure les API Traces</Typography>}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 2 }}>
                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            sx={{
                                borderRadius: 2,
                                py: 1,
                                textTransform: 'none',
                                fontWeight: 'bold',
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                            }}
                        >
                            Appliquer les filtres
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {/* Logs Table */}
            <Fade in timeout={500}>
                <Paper sx={{ borderRadius: 4, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                    <TableContainer>
                        <Table>
                            <TableHead sx={{ bgcolor: '#f8fafc' }}>
                                <TableRow>
                                    <TableCell width={60} />
                                    <TableCell sx={{ fontWeight: 'bold' }}>Niveau</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', display: { xs: 'none', md: 'table-cell' } }}>Date / Heure</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Message</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', display: { xs: 'none', sm: 'table-cell' } }}>Méthode / Path</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', display: { xs: 'none', lg: 'table-cell' } }}>Utilisateur</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {logs.map((log) => (
                                    <React.Fragment key={log._id}>
                                        <TableRow hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                            <TableCell>
                                                <IconButton size="small" onClick={() => setExpandedRow(expandedRow === log._id ? null : log._id)}>
                                                    {expandedRow === log._id ? <ArrowUpIcon /> : <ArrowDownIcon />}
                                                </IconButton>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    icon={getLevelIcon(log.level)}
                                                    label={log.level.toUpperCase()}
                                                    color={getLevelColor(log.level) as any}
                                                    size="small"
                                                    sx={{ fontWeight: 'bold', borderRadius: 1.5, px: 0.5 }}
                                                />
                                                <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'block', md: 'none' }, mt: 0.5 }}>
                                                    {format(new Date(log.timestamp), 'dd/MM HH:mm')}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <CalendarIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                    <Typography variant="body2">{format(new Date(log.timestamp), 'dd/MM/yyyy HH:mm:ss')}</Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                <Typography variant="body2" fontWeight="500">{log.message}</Typography>
                                            </TableCell>
                                            <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography variant="caption" sx={{ bgcolor: 'action.hover', px: 1, py: 0.5, borderRadius: 1, fontWeight: 'bold', color: 'primary.main' }}>
                                                        {log.method}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{log.path}</Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                                                {log.user ? (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Avatar sx={{ width: 24, height: 24, fontSize: 12, bgcolor: theme.palette.primary.light }}>
                                                            {log.user.firstName[0]}
                                                        </Avatar>
                                                        <Typography variant="body2">{`${log.user.firstName} ${log.user.lastName}`}</Typography>
                                                    </Box>
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary" fontStyle="italic">Anonyme</Typography>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                                                <Collapse in={expandedRow === log._id} timeout="auto" unmountOnExit>
                                                    <Box sx={{ p: 3, bgcolor: '#f8fafc', borderTop: '1px solid #edf2f7' }}>
                                                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="primary">
                                                            Détails de l'erreur & Stack Trace
                                                        </Typography>
                                                        <Divider sx={{ mb: 2 }} />

                                                        <Grid container spacing={2} sx={{ mb: 2 }}>
                                                            <Grid size={{ xs: 12, md: 6 }}>
                                                                <Typography variant="caption" display="block" color="text.secondary">Adresse IP</Typography>
                                                                <Typography variant="body2" fontWeight="bold">{log.ip || 'Inconnue'}</Typography>
                                                            </Grid>
                                                            <Grid size={{ xs: 12, md: 6 }}>
                                                                <Typography variant="caption" display="block" color="text.secondary">ID du Log</Typography>
                                                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{log._id}</Typography>
                                                            </Grid>
                                                        </Grid>

                                                        <Paper variant="outlined" sx={{ p: 2, bgcolor: '#1e293b', color: '#e2e8f0', overflowX: 'auto', borderRadius: 2 }}>
                                                            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                                {log.stack || 'Aucune stack trace disponible'}
                                                            </pre>
                                                        </Paper>

                                                        {/* Patch Info Section */}
                                                        <Box sx={{ mt: 3, p: 2, bgcolor: '#fff', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                                                <Typography variant="subtitle2" fontWeight="bold" color="secondary">
                                                                    Suivi de Correction / Patch
                                                                </Typography>
                                                                <Button
                                                                    startIcon={<BuildIcon />}
                                                                    size="small"
                                                                    variant="outlined"
                                                                    onClick={() => handleOpenPatchModal(log)}
                                                                >
                                                                    Gérer le Patch
                                                                </Button>
                                                            </Box>

                                                            {(log.patchComment || log.patchDate || log.patchAnnouncement) ? (
                                                                <Grid container spacing={2}>
                                                                    <Grid size={{ xs: 12, md: 6 }}>
                                                                        <Typography variant="caption" color="text.secondary">Commentaire</Typography>
                                                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{log.patchComment || '-'}</Typography>
                                                                    </Grid>
                                                                    <Grid size={{ xs: 12, md: 3 }}>
                                                                        <Typography variant="caption" color="text.secondary">Date de Patch</Typography>
                                                                        <Typography variant="body2">
                                                                            {log.patchDate ? format(new Date(log.patchDate), 'dd/MM/yyyy HH:mm') : '-'}
                                                                        </Typography>
                                                                    </Grid>
                                                                    <Grid size={{ xs: 12, md: 3 }}>
                                                                        <Typography variant="caption" color="text.secondary">Plateforme</Typography>
                                                                        <Chip label={log.patchPlatform || 'Non spécifié'} size="small" variant="outlined" />
                                                                    </Grid>
                                                                    <Grid size={{ xs: 12 }}>
                                                                        <Typography variant="caption" color="text.secondary">Annonce</Typography>
                                                                        <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                                                                            {log.patchAnnouncement || '-'}
                                                                        </Typography>
                                                                    </Grid>
                                                                </Grid>
                                                            ) : (
                                                                <Typography variant="body2" color="text.secondary" fontStyle="italic">
                                                                    Aucune information de patch renseignée.
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    </React.Fragment>
                                ))}
                                {logs.length === 0 && !loading && (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center" sx={{ py: 10 }}>
                                            <Typography variant="h6" color="text.secondary">Aucun log trouvé</Typography>
                                            <Typography variant="body2" color="text.secondary">Essayez de modifier vos critères de recherche</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[10, 25, 50]}
                        component="div"
                        count={total}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        labelRowsPerPage="Lignes par page"
                        sx={{ bgcolor: '#f8fafc' }}
                    />
                </Paper>
            </Fade>
            <PatchInfoModal
                open={patchModalOpen}
                onClose={() => setPatchModalOpen(false)}
                onSave={handleSavePatch}
                initialData={selectedErrorForPatch || undefined}
            />
        </Box>
    );
};

export default ErrorReporterPage;
